import { useState, useEffect, useCallback, useRef } from 'react'
import axios from 'axios'
import { format, parseISO } from 'date-fns'
import { es, enUS } from 'date-fns/locale'
import { useNavigate } from 'react-router-dom'
import { Button } from '../components/ui/button'
import { Card, CardContent } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { Badge } from '../components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { toast } from 'sonner'
import { useLanguage } from '../context/LanguageContext'
import { cn } from '../lib/utils'
import { 
  Plus, Search, Lightbulb, Inbox, Eye, Rocket, Archive,
  ChevronRight, Sparkles, Building2, Briefcase, User, MoreHorizontal,
  X, Circle
} from 'lucide-react'
import { QuickAddIdeaBucketButton } from '../components/modals/IdeaBucketModal'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`

// Icon map for dynamic bucket icons
const iconMap = {
  Sparkles, Rocket, Building2, Briefcase, User, MoreHorizontal, Circle,
  Lightbulb, Eye, Inbox, Archive
}

const STATUS_CONFIG = {
  inbox: { icon: Inbox, color: 'bg-blue-500', label: 'inbox' },
  review: { icon: Eye, color: 'bg-amber-500', label: 'review' },
  doing: { icon: Rocket, color: 'bg-emerald-500', label: 'doing' },
  archived: { icon: Archive, color: 'bg-gray-500', label: 'archived' },
}

// Default colors for buckets without explicit color
const BUCKET_COLORS = ['#8b5cf6', '#0ea5e9', '#10b981', '#f59e0b', '#6b7280', '#ec4899', '#14b8a6']

export default function CrazyIdeasPage() {
  const { t, locale, formatDate } = useLanguage()
  const navigate = useNavigate()
  const [ideas, setIdeas] = useState([])
  const [buckets, setBuckets] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('inbox')
  const [searchQuery, setSearchQuery] = useState('')
  const [bucketFilter, setBucketFilter] = useState('')
  const [swipingId, setSwipingId] = useState(null)
  const [swipeX, setSwipeX] = useState(0)
  const [statusCounts, setStatusCounts] = useState({ inbox: 0, review: 0, doing: 0, archived: 0 })
  const touchStartX = useRef(0)
  
  const dateLocale = locale === 'es-MX' ? es : enUS

  // Fetch buckets first
  const fetchBuckets = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/idea-buckets`)
      setBuckets(response.data)
    } catch (error) {
      console.error('Error fetching buckets:', error)
    }
  }, [])

  // Fetch counts for all statuses (independent of current filter)
  const fetchStatusCounts = useCallback(async () => {
    try {
      const params = new URLSearchParams()
      if (bucketFilter) params.append('bucket_id', bucketFilter)
      if (searchQuery) params.append('q', searchQuery)
      
      // Fetch all ideas to count (without status filter)
      const response = await axios.get(`${API}/crazy-ideas?${params.toString()}`)
      const allIdeas = response.data
      
      setStatusCounts({
        inbox: allIdeas.filter(i => i.status === 'inbox').length,
        review: allIdeas.filter(i => i.status === 'review').length,
        doing: allIdeas.filter(i => i.status === 'doing').length,
        archived: allIdeas.filter(i => i.status === 'archived').length,
      })
    } catch (error) {
      console.error('Error fetching status counts:', error)
    }
  }, [bucketFilter, searchQuery])

  const fetchIdeas = useCallback(async () => {
    try {
      const params = new URLSearchParams()
      if (activeTab !== 'all') params.append('status', activeTab)
      if (bucketFilter) params.append('bucket_id', bucketFilter)
      if (searchQuery) params.append('q', searchQuery)
      params.append('sort', '-created_at')
      
      const response = await axios.get(`${API}/crazy-ideas?${params.toString()}`)
      setIdeas(response.data)
    } catch (error) {
      console.error('Error fetching ideas:', error)
      toast.error(t('common.error'))
    } finally {
      setLoading(false)
    }
  }, [activeTab, bucketFilter, searchQuery, t])

  useEffect(() => {
    fetchBuckets()
  }, [fetchBuckets])

  useEffect(() => {
    fetchIdeas()
    fetchStatusCounts()
  }, [fetchIdeas, fetchStatusCounts])

  const handleSearch = () => {
    fetchIdeas()
    fetchStatusCounts()
  }

  const updateIdeaStatus = async (ideaId, newStatus) => {
    try {
      await axios.put(`${API}/crazy-ideas/${ideaId}`, { status: newStatus })
      toast.success(t('ideas.ideaUpdated'))
      fetchIdeas()
      fetchStatusCounts()
    } catch (error) {
      toast.error(t('ideas.errorSaving'))
    }
  }

  // Swipe handlers
  const handleTouchStart = (e, ideaId) => {
    touchStartX.current = e.touches[0].clientX
    setSwipingId(ideaId)
  }

  const handleTouchMove = (e) => {
    if (!swipingId) return
    const deltaX = e.touches[0].clientX - touchStartX.current
    setSwipeX(Math.max(-100, Math.min(100, deltaX)))
  }

  const handleTouchEnd = (ideaId) => {
    if (swipeX > 60) {
      // Swipe right - move to review
      updateIdeaStatus(ideaId, 'review')
    } else if (swipeX < -60) {
      // Swipe left - archive
      updateIdeaStatus(ideaId, 'archived')
    }
    setSwipingId(null)
    setSwipeX(0)
  }

  // Get bucket config from dynamic buckets list
  const getBucketConfig = (idea) => {
    if (idea.bucket_id) {
      const bucket = buckets.find(b => b.id === idea.bucket_id)
      if (bucket) {
        const BucketIcon = iconMap[bucket.icon] || Circle
        const colorIndex = buckets.indexOf(bucket) % BUCKET_COLORS.length
        return {
          name: bucket.name,
          icon: BucketIcon,
          color: BUCKET_COLORS[colorIndex]
        }
      }
    }
    // Fallback for ideas without bucket
    return {
      name: idea.bucket_name || locale === 'es-MX' ? 'Sin cubeta' : 'No bucket',
      icon: Circle,
      color: '#6b7280'
    }
  }

  const getStatusLabel = (status) => {
    const config = STATUS_CONFIG[status]
    return config ? t(`ideas.${config.label}`) : status
  }

  const tabs = [
    { key: 'inbox', labelKey: 'ideas.inbox', icon: Inbox },
    { key: 'review', labelKey: 'ideas.review', icon: Eye },
    { key: 'doing', labelKey: 'ideas.doing', icon: Rocket },
    { key: 'archived', labelKey: 'ideas.archived', icon: Archive },
  ]

  if (loading) {
    return (
      <div className="p-4 md:p-8 pb-safe flex items-center justify-center min-h-[50vh]">
        <div className="animate-pulse text-muted-foreground">{t('common.loading')}</div>
      </div>
    )
  }

  return (
    <div className="p-4 md:p-8 pb-safe max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold mb-1 flex items-center gap-2">
            <Lightbulb className="w-7 h-7 text-amber-500" />
            {t('ideas.title')}
          </h1>
          <p className="text-muted-foreground text-sm">{t('ideas.subtitle')}</p>
        </div>
        <Button
          onClick={() => navigate('/ideas/new')}
          size="sm"
          className="rounded-full h-10 px-4 bg-amber-500 hover:bg-amber-600"
          data-testid="new-idea-btn"
        >
          <Plus className="w-4 h-4 md:mr-2" />
          <span className="hidden md:inline">{t('ideas.newIdea')}</span>
        </Button>
      </div>

      {/* Search & Filters */}
      <div className="flex gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('ideas.search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="pl-10"
            data-testid="search-ideas-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <Select value={bucketFilter || "all"} onValueChange={(val) => setBucketFilter(val === "all" ? "" : val)}>
            <SelectTrigger className="w-40" data-testid="bucket-filter">
              <SelectValue placeholder={t('ideas.allBuckets')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('ideas.allBuckets')}</SelectItem>
              {buckets.map((bucket, index) => {
                const BucketIcon = iconMap[bucket.icon] || Circle
                const color = BUCKET_COLORS[index % BUCKET_COLORS.length]
                return (
                  <SelectItem key={bucket.id} value={bucket.id}>
                    <div className="flex items-center gap-2">
                      <BucketIcon className="w-4 h-4" style={{ color }} />
                      {bucket.name}
                    </div>
                  </SelectItem>
                )
              })}
            </SelectContent>
          </Select>
          <QuickAddIdeaBucketButton onBucketCreated={fetchBuckets} />
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 -mx-4 px-4 overflow-x-auto scrollbar-hide">
        <div className="flex gap-2 min-w-max pb-2">
          {tabs.map((tab) => {
            const TabIcon = tab.icon
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={cn(
                  'px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-2',
                  activeTab === tab.key
                    ? 'bg-foreground text-background'
                    : 'bg-secondary text-foreground hover:bg-secondary/80'
                )}
                data-testid={`tab-${tab.key}`}
              >
                <TabIcon className="w-4 h-4" />
                {t(tab.labelKey)} ({statusCounts[tab.key]})
              </button>
            )
          })}
        </div>
      </div>

      {/* Ideas List */}
      <div className="space-y-3">
        {ideas.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Lightbulb className="w-12 h-12 mx-auto mb-4 text-amber-500/50" />
              <p className="text-muted-foreground">{t('ideas.noIdeas')}</p>
              <Button 
                variant="outline" 
                className="mt-4 rounded-full"
                onClick={() => navigate('/ideas/new')}
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('ideas.createFirst')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          ideas.map((idea) => {
            const bucketConfig = getBucketConfig(idea)
            const BucketIcon = bucketConfig.icon
            const statusConfig = STATUS_CONFIG[idea.status]
            const isSwiping = swipingId === idea.id
            
            return (
              <div
                key={idea.id}
                className="relative overflow-hidden"
              >
                {/* Swipe backgrounds - only show on mobile with touch */}
                <div className={cn(
                  "absolute inset-0 flex items-center px-4 transition-opacity pointer-events-none",
                  isSwiping && swipeX > 30 ? "opacity-100" : "opacity-0"
                )} style={{ backgroundColor: '#f59e0b' }}>
                  <Eye className="w-6 h-6 text-white" />
                  <span className="ml-2 text-white font-medium">{t('ideas.review')}</span>
                </div>
                <div className={cn(
                  "absolute inset-0 flex items-center justify-end px-4 transition-opacity pointer-events-none",
                  isSwiping && swipeX < -30 ? "opacity-100" : "opacity-0"
                )} style={{ backgroundColor: '#6b7280' }}>
                  <span className="mr-2 text-white font-medium">{t('ideas.archived')}</span>
                  <Archive className="w-6 h-6 text-white" />
                </div>
                
                <Card 
                  className={cn(
                    "cursor-pointer transition-transform",
                    isSwiping ? "" : "hover:border-amber-500/50"
                  )}
                  style={{ transform: isSwiping ? `translateX(${swipeX}px)` : 'none' }}
                  onClick={() => !isSwiping && navigate(`/ideas/${idea.id}`)}
                  onTouchStart={(e) => handleTouchStart(e, idea.id)}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={() => handleTouchEnd(idea.id)}
                  data-testid={`idea-card-${idea.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${bucketConfig.color}20` }}
                      >
                        <BucketIcon className="w-5 h-5" style={{ color: bucketConfig.color }} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium truncate">
                            {idea.title || idea.body.slice(0, 50) + (idea.body.length > 50 ? '...' : '')}
                          </h3>
                          <Badge 
                            variant="outline" 
                            className={cn("text-xs px-2 py-0", statusConfig?.color, "text-white border-0")}
                          >
                            {getStatusLabel(idea.status)}
                          </Badge>
                        </div>
                        
                        {idea.title && (
                          <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                            {idea.body}
                          </p>
                        )}
                        
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs text-muted-foreground">
                            {format(parseISO(idea.created_at), 'd MMM', { locale: dateLocale })}
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-secondary">
                            {bucketConfig.name}
                          </span>
                          {idea.tags?.slice(0, 3).map(tag => (
                            <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600">
                              #{tag}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            )
          })
        )}
      </div>

      {/* Swipe hint for mobile - only show if there are ideas */}
      {ideas.length > 0 && activeTab === 'inbox' && (
        <p className="text-center text-xs text-muted-foreground mt-6 md:hidden">
          {t('ideas.swipeRightReview')} • {t('ideas.swipeLeftArchive')}
        </p>
      )}
    </div>
  )
}
